import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { t } from "@/i18n";

function StatCard({ label, value, testid, accent }) {
  return (
    <div data-testid={testid} className="surface-1 rounded-md p-6">
      <div className="text-[11px] text-[var(--text-muted)] mb-3 font-light tracking-wide">{label}</div>
      <div className={`font-editorial text-5xl leading-none ${accent ? "text-[var(--gold)]" : "text-[var(--text-primary)]"}`}>{value}</div>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/analytics");
      setData(data);
    })();
  }, []);

  if (!data) return <div className="p-10 text-sm text-[var(--text-muted)]">{t("common.loading")}</div>;

  const maxGuests = Math.max(...data.seven_days.map((d) => d.guests), 1);

  return (
    <div className="px-6 lg:px-12 py-12 max-w-6xl mx-auto">
      <header className="mb-12 animate-fade-up">
        <h1 className="font-editorial italic text-5xl sm:text-6xl leading-[1.05] tracking-tight text-[var(--text-primary)]" data-testid="analytics-title">{t("analytics.title")}</h1>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard testid="analytics-total" label={t("analytics.total_reservations")} value={data.total_reservations} accent />
        <StatCard testid="analytics-completed" label={t("analytics.completed")} value={data.completed} />
        <StatCard testid="analytics-noshows" label={t("analytics.no_shows")} value={data.no_shows} />
        <StatCard testid="analytics-returning" label={t("analytics.returning")} value={data.returning_guests} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="surface-1 rounded-md p-6">
          <h2 className="font-editorial italic text-2xl text-[var(--text-primary)] mb-6">{t("analytics.seven_days")}</h2>
          <div className="flex items-end gap-2 h-48">
            {data.seven_days.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2" data-testid={`analytics-day-${i}`}>
                <div className="text-[10px] text-gold font-heading">{d.guests || ""}</div>
                <div
                  className="w-full bg-gradient-to-t from-gold/40 to-gold/10 border border-gold/30 rounded-sm transition-all hover:from-gold/60"
                  style={{ height: `${(d.guests / maxGuests) * 100}%`, minHeight: "2px" }}
                />
                <div className="text-[10px] text-[var(--text-muted)]">{new Date(d.date).toLocaleDateString("de-CH", { weekday: "short" })}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="surface-1 rounded-md p-6">
          <h2 className="font-editorial italic text-2xl text-[var(--text-primary)] mb-6">{t("analytics.peak_hours")}</h2>
          <div className="space-y-3">
            {data.peak_hours.length === 0 && <div className="text-sm text-[var(--text-dim)]">Noch keine Daten.</div>}
            {data.peak_hours.map((h, i) => {
              const max = data.peak_hours[0]?.count || 1;
              const pct = (h.count / max) * 100;
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`analytics-peak-${i}`}>
                  <div className="font-heading text-sm text-gold w-12">{h.hour}</div>
                  <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-gold/60 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-xs text-[var(--text-muted)] w-8 text-right">{h.count}</div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
